import LegalPageLayout from '../../components/legal/LegalPageLayout'

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="SynthetIQ Signals®, Inc."
      effectiveDate="To be set upon legal review — not yet in effect"
      lastUpdated="2026-09-25 (draft)"
      bannerText="Placeholder — pending legal team review. This page describes our data practices to the best of our current knowledge. It has not been reviewed or approved by legal counsel and should not be relied on as final. Do not treat any date, address, or certification claim on this page as confirmed until that review is complete."
      intro={[
        'SynthetIQ Signals®, Inc. ("SynthetIQ Signals®," "we," "us," or "our") is committed to protecting your privacy and the security of your personal data. This Privacy Policy describes how we collect, use, disclose, and protect personal data when you use our website at synthetiqsignals.com (the "Site"), the 23Controls™ platform and Graffiti™ OS (the "Platform"), and related services (collectively, the "Services").',
        'SynthetIQ Signals®, Inc. operates 23Controls™ and Graffiti™ OS under license from Mimeograph Holdings, LLC, which owns the underlying intellectual property. "23Controls™" and "Graffiti™ OS" are product and platform names, not separate legal entities — SynthetIQ Signals®, Inc. is the company responsible for your data under this Policy.',
      ]}
      relatedLinks={[
        { to: '/corporate/legal/privacy/graffiti-os', label: 'Graffiti™ OS Privacy Policy (product supplement)' },
        { to: '/corporate/legal/cookies', label: 'Cookies Policy' },
        { to: '/corporate/legal/terms', label: 'Terms of Service' },
      ]}
      sections={[
        {
          heading: 'Data Privacy Framework Compliance',
          paragraphs: [
            'SynthetIQ Signals® intends to comply with the EU-U.S. Data Privacy Framework (EU-U.S. DPF), the UK Extension to the EU-U.S. DPF, and the Swiss-U.S. Data Privacy Framework (Swiss-U.S. DPF) as set forth by the U.S. Department of Commerce (collectively, the "DPF").',
          ],
          note: 'Status as of this draft: the DPF self-certification submission is in progress but not yet complete. Per the Department of Commerce\'s own instructions, this policy must not claim actual DPF participation/adherence until Commerce notifies the organization it may do so — the "intends to comply" phrasing above is deliberate, not a placeholder to tighten casually. Once Commerce approves the submission, this section should be updated to the adherence statement Commerce requires, and only then.',
        },
        {
          heading: '1. Scope of This Policy',
          paragraphs: ['This Privacy Policy applies to:'],
          list: [
            'Personnel Data: current, former, and prospective partners, employees, and contractors.',
            'Client and User Data: current, former, and prospective clients and Platform users.',
            'Third-Party Data: suppliers, service providers, and other business relationships.',
            'Public Inquiries: data collected from the general public to answer inquiries.',
            'AI and Machine Learning Data: data processed through AI-powered features within the Platform.',
          ],
        },
        {
          heading: '2. Information We Collect',
          paragraphs: [
            '2.1 Information you provide directly: full name, email, phone number, company/organization and job title, billing and payment information, username and password; profile photo and social handles; files, documents, and other content or messages you submit to the Platform.',
            '2.2 Information collected automatically: IP address and city-level location, browser and device information, pages visited and features used, clickstream and API request logs.',
            '2.3 Information from third parties: basic profile information if you sign in via Google, Microsoft, or GitHub SSO; information shared by integration or channel partners; publicly available business information.',
            '2.4 AI-processed information: the Platform uses AI/ML to analyze and categorize content, generate insights, and detect security threats. We indicate when AI processing is in use — see "AI and Machine Learning Practices" below.',
          ],
        },
        {
          heading: '3. How We Use Your Information',
          list: [
            'Service delivery: creating and managing your account, providing Platform access, processing transactions, delivering support.',
            'Service improvement: analyzing usage, developing features, improving AI models (with safeguards described below).',
            'Communications: service notifications, responses to inquiries, marketing with consent.',
            'Security and compliance: fraud prevention, network security, legal compliance, enforcing our Terms of Service.',
          ],
        },
        {
          heading: '4. Legal Bases for Processing (GDPR)',
          paragraphs: ['For individuals in the EEA, UK, and Switzerland, we process personal data on these bases:'],
          list: [
            'Performance of Contract — processing necessary to fulfill our obligations to you.',
            'Legitimate Interests — where not overridden by your rights.',
            'Consent — freely given, specific, informed.',
            'Legal Obligation — where required by applicable law.',
          ],
        },
        {
          heading: '5. How We Share Your Information',
          list: [
            'Service providers who perform services on our behalf (hosting, payment processing, analytics, support, security) under contractual data-protection obligations.',
            'Affiliates, including Mimeograph Holdings, LLC, for business operations and service delivery.',
            'In connection with a merger, acquisition, or sale of assets.',
            'When required by law, regulation, or legal process.',
          ],
          note: 'The cookies/analytics vendor list on the Cookies Policy page is only partially confirmed against what actually runs on this site today — see that page for detail. Don\'t assume every service-provider category above maps to a currently-integrated vendor.',
        },
        {
          heading: '6. Accountability for Onward Transfers',
          paragraphs: [
            'Consistent with the DPF Principles, we will only disclose personal data to third parties under the following conditions: to a service provider acting on our behalf, consistent with the purpose for which the data was collected; when required by applicable law, regulation, or legal process; in connection with a corporate transaction; or with your explicit consent.',
            'In the context of onward transfers, SynthetIQ Signals®, Inc. remains responsible for personal data it receives under the DPF and subsequently transfers to third parties acting as our agents, and remains liable under the DPF if those agents process the data in a manner inconsistent with the DPF Principles, unless we can prove we are not responsible for the event giving rise to the damage.',
          ],
        },
        {
          heading: '7. Data Security',
          list: [
            'Encryption in transit and at rest.',
            'Multi-factor authentication and access controls.',
            'Regular security assessments.',
            'Employee security training and incident response procedures.',
          ],
          paragraphs: ['No method of transmission or storage is 100% secure. We cannot guarantee absolute security.'],
        },
        {
          heading: '8. Data Retention',
          list: [
            'Active account data: retained for the duration of your account plus a reasonable reactivation period.',
            'Transaction records: retained per applicable tax and financial regulations (typically 7 years).',
            'Marketing data: retained until you withdraw consent or opt out.',
            'Legal hold data: retained for the duration of any litigation or investigation.',
          ],
          note: 'These retention windows are the standard categories from the original draft, not independently re-verified against the actual system\'s configured retention settings in this pass.',
        },
        {
          heading: '9. AI and Machine Learning Practices',
          paragraphs: [
            'The Platform incorporates AI and machine learning capabilities. We aim to indicate clearly when AI-powered features are in use. AI features may process content you submit, usage patterns (anonymized/aggregated), and feedback on AI-generated outputs.',
            'We do not use your identifiable content or confidential business information to train AI models for other customers without your explicit consent.',
          ],
          list: [
            'You may opt out of certain AI-powered features where technically feasible.',
            'You may request human review of significant AI-assisted decisions.',
            'You may request information about AI processing of your data.',
          ],
          note: 'This section intentionally does not describe any blockchain-based or on-chain data governance mechanism. As of this draft, no such mechanism processes data for any current SynthetIQ Signals®, 23Controls™, or Graffiti™ OS user account — see the accompanying policy-reality-check analysis for why that omission is deliberate, not an oversight.',
        },
        {
          heading: '10. International Data Transfers',
          paragraphs: [
            'SynthetIQ Signals®, Inc. is headquartered in the United States. Personal data may be transferred to and processed in the United States and other countries where we or our service providers operate.',
          ],
        },
        {
          heading: '11. Your Privacy Rights',
          list: [
            'Access, correct, or delete the personal data we hold about you.',
            'Opt out of marketing communications.',
            'EEA/UK/Switzerland: data portability, restriction, objection, and consent withdrawal, and the right to lodge a complaint with your local authority.',
            'California residents (CCPA/CPRA): the right to know, delete, correct, and opt out of sale/sharing of personal information, and non-discrimination for exercising these rights. We do not sell personal information as defined by California law.',
          ],
          paragraphs: [
            'To exercise your rights, contact privacy@synthetiq.io. We will verify your identity before processing your request and aim to respond within 30 days (GDPR) or 45 days (CCPA).',
          ],
        },
        {
          heading: "12. Children's Privacy",
          paragraphs: [
            'Our Services are not directed to individuals under age 16. We do not knowingly collect personal data from children under 16.',
          ],
        },
        {
          heading: '13. Dispute Resolution',
          paragraphs: [
            '13.1 Contact Us First — If you have an inquiry or complaint about our handling of personal data received under the DPF, please first contact our Privacy Office at privacy@synthetiq.io.',
            '13.2 Independent Dispute Resolution — In compliance with the DPF, SynthetIQ Signals®, Inc. commits to refer unresolved complaints concerning personal data (other than human resources data) to JAMS, an alternative dispute resolution provider based in the United States. If we have not addressed your complaint to your satisfaction, visit jamsadr.com/dpf-dispute-resolution for more information or to file a complaint. JAMS services are provided at no cost to you.',
            '13.3 Regulatory Oversight — SynthetIQ Signals®, Inc. is subject to the investigatory and enforcement powers of the U.S. Federal Trade Commission.',
          ],
          note: 'JAMS is named here because it\'s the recourse mechanism being selected on the DPF self-certification form itself — the two have to agree. If a different provider ends up selected there instead, update this section to match before either is finalized.',
        },
        {
          heading: '14. Third-Party Links',
          paragraphs: [
            'Our Site and Platform may contain links to third-party websites or services not operated by us. This Privacy Policy does not apply to those third-party services. We are not responsible for the privacy practices or content of third-party services.',
          ],
        },
        {
          heading: '15. Updates to This Policy',
          paragraphs: [
            'We may update this Privacy Policy to reflect changes in our practices, technologies, or legal requirements. We will not make material changes that reduce your rights without appropriate notice, and will post the updated policy with a new "Last Updated" date.',
          ],
        },
        {
          heading: '16. Contact Information',
          paragraphs: [
            'SynthetIQ Signals®, Inc.',
            'Attn: Privacy Office',
            'Email: privacy@synthetiq.io',
          ],
          note: 'Mailing address intentionally left out of this draft rather than inventing one — add the real registered address before this page is treated as final.',
        },
      ]}
    />
  )
}
